/**
 * Shared animation utilities for field status change animations
 * Used by QuestionsToGo and ExtractedFieldsDisplay
 */

import { useRef, useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { HIGHLIGHT_PULSE_DURATION, HIGHLIGHT_PULSE_COLOR } from '../components/shared/cssConstants';

/**
 * Custom hook to detect newly answered fields and provide highlight state.
 * Tracks the previous answeredFields set and returns which fields just changed.
 * Auto-clears the highlight after the animation duration.
 * 
 * @param answeredFields - Current set of answered field names
 * @returns Set of field names that were just answered (for highlight animation)
 */
export function useFieldChangeHighlight(answeredFields: Set<string>): Set<string> {
  const prevAnsweredRef = useRef<Set<string>>(new Set());
  const [recentlyChanged, setRecentlyChanged] = useState<Set<string>>(new Set());

  useEffect(() => {
    const prev = prevAnsweredRef.current;
    const newlyChanged = new Set<string>();

    answeredFields.forEach(field => {
      if (!prev.has(field)) newlyChanged.add(field);
    });

    if (newlyChanged.size > 0) {
      setRecentlyChanged(newlyChanged);
      const timer = setTimeout(() => setRecentlyChanged(new Set()), HIGHLIGHT_PULSE_DURATION);
      return () => clearTimeout(timer);
    }

    prevAnsweredRef.current = new Set(answeredFields);
  }, [answeredFields]);

  // Always update ref
  useEffect(() => {
    prevAnsweredRef.current = new Set(answeredFields);
  }, [answeredFields]);

  return recentlyChanged;
}

/**
 * CSS keyframes for the highlight pulse animation.
 * Inject this once in a <style> tag inside the scrollable container.
 */
export const HIGHLIGHT_PULSE_CSS = `
  @keyframes highlightPulse {
    0% { box-shadow: inset 0 0 0 2px ${HIGHLIGHT_PULSE_COLOR}; }
    50% { box-shadow: inset 0 0 0 2px ${HIGHLIGHT_PULSE_COLOR}; }
    100% { box-shadow: inset 0 0 0 0px transparent; }
  }
`;

/**
 * Get the highlight animation style for a field item.
 * Apply this via spread: style={{ ...baseStyle, ...getHighlightStyle(isHighlighted) }}
 * 
 * @param isHighlighted - Whether this field was just answered
 * @returns CSSProperties with animation if highlighted, empty object otherwise
 */
export function getHighlightStyle(isHighlighted: boolean): CSSProperties {
  if (!isHighlighted) return {};
  return {
    animation: `highlightPulse ${HIGHLIGHT_PULSE_DURATION}ms ease-out`,
  };
}
